/* Grass, as PATCHES rather than blades.
 *
 * The first attempt scattered individual eight-pixel tufts by blue noise. The
 * distribution was right and the result looked wrong, for a reason worth
 * writing down: at this scale a single blade is three or four lit pixels, and
 * a field of isolated three-pixel marks does not read as grass — it reads as
 * dirt with specks on it. Grass is a MASS. What tells you a thing is a lawn is
 * that it covers ground continuously and has an edge.
 *
 * So this is a coverage layer, not a prop layer: a world-space noise field
 * decides which tiles are grassed, and each grassed tile is painted with a
 * patch whose edges are eroded on whichever sides have no grass beside them.
 * Adjacent patches therefore merge into one shape with a ragged border, which
 * is what a patch of grass actually looks like from above.
 *
 * The props — flowers, mushrooms, pebbles, bones — stay in `decals.js` and are
 * drawn ON TOP of this. Those read fine as individual things, because they
 * are individual things.
 */
import { RNG, mix } from './rng.ts';
import { T, TS, UI } from './art.ts';
import { isWet } from './passable.ts';

/** Tiles grass will take. */
const SOIL = new Set([T.GROUND, T.MOSS]);

/** How much of the world is grassed, per biome, and how lime it is. A fen is
 *  lush, a scree is nearly bare, and the ash grows almost nothing. */
export const TURF = {
  /* LUSH AND PLENTIFUL (Vijay, 2026-09-05: 'grass looks worse than before it's all patchy and cut off now i want it to look lush and plentiful').
   * Cover is most of the soil in the green biomes, and the tall stand starts a third of the way into a patch, not past its middle. */
  heath: { cover: 0.72, lime: 0.85 },
  bloom: { cover: 0.86, lime: 1.0 },
  fen:   { cover: 0.78, lime: 0.7 },
  dust:  { cover: 0.20, lime: 0.55 },
  scree: { cover: 0.12, lime: 0.5 },
  ash:   { cover: 0.08, lime: 0.35 },
};

const smooth = (t) => t * t * (3 - 2 * t);
const lat = (s, a, b) => ((mix(s, a + 1013, b + 2027) >>> 8) / 16777216);
/** Value noise on the world lattice, so a patch runs across a screen seam
 *  instead of stopping at it. */
export function field(seed, x, y, scale) {
  const ix = Math.floor(x * scale), iy = Math.floor(y * scale);
  const u = smooth(x * scale - ix), v = smooth(y * scale - iy);
  const a = lat(seed, ix, iy), b = lat(seed, ix + 1, iy);
  const c = lat(seed, ix, iy + 1), d = lat(seed, ix + 1, iy + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

/** Nearby pixels share four lattice corners; retain only the current cell. */
export function fieldSampler(seed, scale) {
  let lastX = NaN, lastY = NaN, a = 0, b = 0, c = 0, d = 0;
  return (x, y) => {
    const ix = Math.floor(x * scale), iy = Math.floor(y * scale);
    if (ix !== lastX || iy !== lastY) {
      a = lat(seed, ix, iy); b = lat(seed, ix + 1, iy);
      c = lat(seed, ix, iy + 1); d = lat(seed, ix + 1, iy + 1);
      lastX = ix; lastY = iy;
    }
    const u = smooth(x * scale - ix), v = smooth(y * scale - iy);
    return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
  };
}

/** Which tiles of a screen are grassed. Two octaves: the coarse one makes
 *  meadows several tiles across, the fine one ruffles their edges so a patch
 *  is not an oval. */
/* `coverAt` IS WHY THE GRASS STOPPED IN A RULED LINE.
 *
 * How much of the ground a country's grass covers runs from 0.08 in ash to
 * 0.66 in bloom -- eightfold -- and it was read from ONE key for the whole
 * screen while the noise it thresholds is a world lattice, deliberately, so a
 * patch runs across a seam instead of stopping at it. A continuous field under
 * a threshold that jumps at a screen column gives exactly one thing: a
 * dead-straight edge in the grass, the full height of the map, on the screen
 * grid. Measured by the whole-island probe as a 28.9-luma column seam against a
 * mid-screen control of 2, found independently by an art critic reading the
 * render -- "a 400px vertical edge holding to five pixels" -- and it was the
 * most conspicuous straight line left in the picture.
 *
 * The ground palette solved this already by asking `biomeBlendAt` per TILE and
 * mixing the two countries; the grass now reads its cover from the same place,
 * so the threshold moves as smoothly as the field does. */
/** THE FIELD THE GRASS GROWS ALONG, at any point, not just at a tile centre.
 *
 * WARPED, OR THE PATCHES ARE THE NOISE LATTICE. `field` is value noise on an
 * axis-aligned lattice, and `cover` in the drier countries is small -- 0.12 in
 * scree, 0.08 in ash -- so the test against it keeps only the top eighth of it.
 * The top eighth of smoothstepped value noise does not look like blobs: it
 * clings to the lattice cells, so at scale 0.11 (a cell every nine tiles) the
 * grass came out as bands with dead-straight vertical edges running the height
 * of the map. One measured a 29-luma column against a mid-screen control of 2,
 * and an art critic reading the whole island called it "a dead-straight
 * 4.5-tile corridor" and ranked it fourth of seventeen defects.
 *
 * Two octaves of warp on the sample point, the same device the biome field uses,
 * and the contours meander instead. The warp is in WORLD coordinates so a patch
 * is the same patch from either side of a seam, which is what this field is for.
 *
 * `wx`/`wy` are world TILES and may be fractional: `growTurf` asks at whole
 * tiles to decide which cells exist, and `bakeTurf` asks between them to find
 * where inside a tile the patch actually ends. */
export function turfNoise(seedNum, wx, wy) {
  /* THE WARP WAS 17 TILES AND THAT IS A CAMOUFLAGE PATTERN.
   *
   * Displacing the sample point by up to seventeen tiles at a wavelength of
   * eighteen folds the field back over itself: the contours stop meandering and
   * start hooking, and a threshold taken near the middle of the distribution --
   * which is where heath's 0.52 cover puts it -- then traces long concave lobes
   * that curl round on themselves. On the whole island those read as weird
   * concave shapes lying on the ground rather than as meadows, and they were
   * reported from play in exactly those words.
   *
   * Nine tiles still breaks up the lattice the warp was added for (the check is
   * the column-seam statistic the map probe prints, which stays under 2 luma at
   * this amplitude) and leaves the contour convex enough to read as a patch.
   *
   * The base octave is also finer: at 0.11 a cell is nine tiles, so a country's
   * grass is three or four enormous blobs. At 0.16 it is a dozen meadows. */
  const wax = wx + (field(seedNum + 911, wx, wy, 0.055) - 0.5) * 9
    + (field(seedNum + 941, wx, wy, 0.17) - 0.5) * 5;
  const way = wy + (field(seedNum + 977, wx, wy, 0.055) - 0.5) * 9
    + (field(seedNum + 991, wx, wy, 0.17) - 0.5) * 5;
  return field(seedNum + 3301, wax, way, 0.16) * 0.7
    + field(seedNum + 7717, wax, way, 0.42) * 0.3;
}

export function growTurf(tiles, biome, cols, rows, seedNum, ox, oy, coverAt = null) {
  const turf = TURF[biome] || TURF.heath;
  const grid: any = [];
  /* THE THRESHOLD OF EVERY SOIL TILE, grassed or not, carried beside the grid.
   *
   * `bakeTurf` needs it to find where inside a tile the patch ends, and it
   * needs it on the BARE side of the boundary too -- otherwise the contour is
   * still cut off at whichever tile happened to fail the test, which is the
   * tile silhouette this is here to remove. Hung on the array rather than
   * threaded through the signature: every caller passes the grid straight to
   * the bake, and `cover` is a world field neither of them should have to
   * recompute. */
  const thr: any = [];
  for (let ty = 0; ty < rows; ty++) {
    const row = [];
    const thrRow: any = [];
    for (let tx = 0; tx < cols; tx++) {
      if (!SOIL.has(tiles[ty][tx])) { row.push(null); thrRow.push(null); continue; }
      /* THE GROUND IS TRAMPLED ROUND A KILL (2026-09-13, round53.ts): the meadow is mostly tall grass now (LS-378), and
       * the pack's bones under it read as a pale fleck between blades -- the blades of the cell below rise over the
       * mark. No grass grows in the eight cells round a kill mark; the bare ring is where the pack ate. Grown here,
       * in the bake, so a re-bake and a reload keep it, and it regrows with the mark's own removal. */
      let trampled = false;
      for (let dy = -1; dy <= 1 && !trampled; dy++) for (let dx = -1; dx <= 1 && !trampled; dx++) { const r = tiles[ty + dy]; if (r && r[tx + dx] === T.BONES) trampled = true; }
      if (trampled) { row.push(null); thrRow.push(null); continue; }
      const wx = ox + tx, wy = oy + ty;
      const v = turfNoise(seedNum, wx, wy);
      const cover = coverAt ? coverAt(wx, wy) : turf.cover;
      thrRow.push(1 - cover);
      if (v < 1 - cover) { row.push(null); continue; }
      // HOW FAR INSIDE THE PATCH THIS IS. Grass is tallest in the middle of a
      // stand and short at its edge, which is what stops a patch reading as a
      // slab with a hard rim.
      let inside = Math.min(1, (v - (1 - cover)) / 0.22);
      // ...AND TALLEST AT THE WATERLINE. Reeds used to be a separate scattered
      // prop and read as scratches; a stand of grass that grows tall where it
      // meets water is the same idea drawn as a mass, which is the only way
      // anything blade-shaped works at sixteen pixels.
      for (const [dx, dy] of ([[1, 0], [-1, 0], [0, 1], [0, -1]]) as Array<[number, number]>) {
        const nx = tx + dx, ny = ty + dy;
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
        const n = tiles[ny][nx];
        if (isWet(n)) { inside = Math.min(1, inside + 0.5); break; }
      }
      row.push({ bend: 0, dir: 1, push: 0, cut: 0,
        v: (mix(seedNum, wx, wy) >>> 3) & 3,
        inside, tall: inside > TALL_AT });
    }
    grid.push(row);
    thr.push(thrRow);
  }
  grid.thr = thr;
  return grid;
}

/** The four-bit neighbour mask for a tile: which sides also have grass. The
 *  patch is eroded on the sides that do not, which is what makes two adjacent
 *  patches read as one shape rather than two squares. */
export function edgesOf(grid, tx, ty, cols, rows) {
  let m = 0;
  if (ty > 0 && grid[ty - 1][tx]) m |= 1;
  if (tx < cols - 1 && grid[ty][tx + 1]) m |= 2;
  if (ty < rows - 1 && grid[ty + 1][tx]) m |= 4;
  if (tx > 0 && grid[ty][tx - 1]) m |= 8;
  return m;
}

/** Blend two hex colours. The palettes are authored per biome and the lime is
 *  a property of the plant, so the patch is a mixture of the two rather than
 *  either one. */
export function mixHex(a, b, t) {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  const out = pa.map((v, i) => Math.round(v + (pb[i] - v) * Math.max(0, Math.min(1, t))));
  return '#' + out.map((v) => v.toString(16).padStart(2, '0')).join('');
}

/* BAKED IN WORLD COORDINATES, NOT PER TILE.
 *
 * The first version cached a 16x16 patch per (variant, edges) and blitted it.
 * It tiled — faintly, but visibly: every patch drew its own dither from its
 * own RNG, so the texture restarted at each tile boundary and the grid showed
 * through the grass. That is the same seam fault this project has chased out
 * of the water and the sand, arriving by a new route.
 *
 * The whole screen's turf is baked once on load, with every pixel's dither
 * taken from its WORLD position — so the texture is continuous across tiles
 * and across screen seams, and a patch spanning six tiles is one piece of
 * grass rather than six.
 *
 * Two canvases, because the bend has to move the tops and not the roots: the
 * roots go down in one blit and the blades are drawn per grassed tile, offset
 * by that tile's lean.
 */
const BLADE_H = 6;
/** How far into a patch the tall stand begins (0..1 of the field's depth). Was 0.55: more than half of every meadow was short marks on flat ground, and it read as patchy. */
export const TALL_AT = 0.28;

/** Hash a world pixel to a stable 0..1.
 *
 *  NOT `mix(seed, x, y)`. Measured: with the coordinates passed straight in,
 *  the mean absolute difference between neighbours is 0.392 along x and
 *  **0.032 along y** — the y term is washed out, so every column repeats all
 *  the way down and the grass came out in vertical stripes. Folding both axes
 *  into each argument with large odd multipliers first gives 0.33 on both,
 *  which is what independence looks like. */
const px = (seed, x, y) => ((mix(seed,
  ((x * 374761393) ^ (y * 668265263)) >>> 0,
  ((y * 2246822519) ^ (x * 3266489917)) >>> 0) >>> 8) / 16777216);

/* NO TWO MEADOWS ARE THE SAME GREEN.
 *
 * `lime` is per BIOME, so every heath screen in a world was painted from
 * exactly the same three colours and a wood of forty screens read as one
 * repeated tile of colour however varied its shapes were. Shape variety had
 * already been fixed twice — crowns, then rock masses — and the world still
 * looked uniform, because the thing that was repeating was the PALETTE.
 *
 * Each screen now drifts its greens a little, warm or cool, by a value drawn
 * from its own world position. Neighbours differ slightly, distant screens
 * differ more often, and a screen keeps its colour when you walk back onto
 * it. The drift is deliberately small: this is meant to read as light and
 * soil changing across a wood, not as a biome boundary every 256 pixels.
 *
 * THE CEILING IS A CONTRAST FLOOR, NOT TASTE. The player has to stay legible
 * on every ground in the game — `test/soak.ts` measures the body against
 * every palette tone at the WCAG 3:1 non-text floor — so `DRIFT` is bounded
 * and the check is run against the drifted extremes, not just the authored
 * colours. Raising it means re-running that check, not just liking it more.
 */
/* WITHIN A SCREEN AS WELL AS BETWEEN THEM.
 *
 * Drifting each screen as a whole is real variety and you cannot see it: you
 * only meet it at a seam, and by then the last screen is off the glass. A
 * meadow that fills the view is still one flat green. What reads as a living
 * ground is PATCHES — soil, damp, light through a canopy — so the drift is
 * also sampled across the screen at about two-and-a-half tiles, on top of the
 * screen's own warm/cool bias.
 *
 * Smaller than the per-screen drift, deliberately: this is meant to be the
 * grain of one meadow, not a map of different ones. It costs nothing per
 * frame — the turf is a bake, drawn once when the screen loads. */
export const PATCH = 0.19;

/* 0.16 WAS INVISIBLE, AND A CHECK SAID IT WAS FINE.
 *
 * At 0.16 two side-by-side screens differed by a mean of 7 RGB units, which
 * is about the threshold of noticing on a lit panel — and the check that was
 * supposed to prove variety counted DISTINCT HEX VALUES, so a one-unit
 * difference across 64 screens scored 20 distinct greens and passed. It was
 * measuring that the arithmetic ran, not that anything looked different.
 * Reported from play twice as the grass and leaves not varying.
 *
 * The ceiling is the player's legibility, and it was never 0.2: measured
 * against every ground tone in every biome at both drift extremes, the worst
 * body-to-ground contrast is 3.27:1 at this value and does not fall below
 * 3.3:1 even at 0.50 — the drift mixes toward mid-tones, so it moves hue far
 * more than luminance. The old `DRIFT <= 0.2` check was taste written as a
 * constraint, and it was the thing keeping the variety invisible. */
export const DRIFT = 0.32;
const WARM = '#cbb06a', COOL = '#4f8f86';

/** The drift here, -1 (cool) to +1 (warm), as a slow field over WORLD TILES.
 *
 * THIS USED TO BE A HASH OF THE SCREEN. `px(seed, ox, oy)` on a screen index
 * gives every screen an independent tone up to DRIFT (0.32) away from the
 * palette, applied to the whole canopy and all the turf on it — so the world
 * was painted as a grid of differently tinted rectangles, with a full random
 * step at every seam. That was variety BETWEEN ROOMS, and it was invisible
 * while a room was all you could see at once; on an open map it is the
 * loudest thing in the picture after the wall itself.
 *
 * A slow interpolated field over world tiles keeps the variety — the world
 * still warms and cools as you cross it — and spends it over about three
 * screens instead of at a boundary. The two callers disagreed about units
 * (canopy passed screen indices, turf passed tile coordinates), which is
 * exactly the kind of thing a per-screen constant hides; both pass world
 * tiles now. */
const DRIFT_CELL = 44;      // tiles: a little under three screens
export function screenDrift(seedNum, tx, ty) {
  if ((globalThis as any).__flatDrift) return 0;   // the diagnostic arm: the same terrain with no warm/cool drift at all
  const cell = DRIFT_CELL;
  const fx = tx / cell, fy = ty / cell;
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const ax = fx - x0, ay = fy - y0;
  const sx = ax * ax * (3 - 2 * ax), sy = ay * ay * (3 - 2 * ay);
  const a = px(seedNum + 9917, x0, y0), b = px(seedNum + 9917, x0 + 1, y0);
  const c = px(seedNum + 9917, x0, y0 + 1), d = px(seedNum + 9917, x0 + 1, y0 + 1);
  return ((a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy) * 2 - 1;
}

/** Push a colour along the warm/cool axis by `d`, bounded by `DRIFT`. */
export function drifted(hex, d, amount = DRIFT) {
  return d >= 0 ? mixHex(hex, WARM, d * amount) : mixHex(hex, COOL, -d * amount);
}

/* THE DRIFT IS A FIELD, AND IT WAS BEING ASKED ONCE A SCREEN (2026-09-08; Vijay: "why is there a
 * seam in the middl here", and twice before that "the lighting is still just suddnetly changing
 * when i move across differeht sections of the map").
 *
 * `screenDrift` is smooth -- a 44-tile cell -- and every caller sampled it AT THE SCREEN'S CORNER
 * and used the one number for the whole screen. So a gradient that moves 0.3 luma from one corner
 * of a screen to the other was being rendered as a flat tint per screen with a STEP between them,
 * and the step is up to 8 luma on the grass alone. Measured across the worst vertical boundary in
 * each of five worlds, with the camera on it: the boundary row's luma step was 6.4x the window's
 * median row-to-row step, against 3.3x for a mid-screen row. With the drift flattened to zero on
 * the same terrain, 2.4x against 2.8x -- the boundary stops being findable. That is the whole bug:
 * not the drift, which is wanted, but asking a field one question per screen.
 *
 * Asking it per tile would mean mixing a colour per tile, and these palettes are read PER PIXEL in
 * the bake. So the axis is quantised instead: one span covering the screen drift and the patch
 * drift together, 65 steps over it -- about 0.4 luma a step, under what an eye finds -- and the
 * colours are made lazily and kept. Within one screen the drift spans well under a single step, so
 * a screen still builds one or two colours per palette, which is what it built before. */
export const SHADES = 65;
const SHADE_SPAN = DRIFT + PATCH;
/** The step a warm/cool shift of `s` falls in. */
export const shadeStep = (s) => {
  const i = Math.round((s / SHADE_SPAN + 1) * 0.5 * (SHADES - 1));
  return i < 0 ? 0 : i > SHADES - 1 ? SHADES - 1 : i;
};
/** A palette that answers by step, mixing each colour once and keeping it. */
export function shadeTable(make) {
  const kept = new Map();
  return (i) => {
    let v = kept.get(i);
    if (v === undefined) { v = make(((i / (SHADES - 1)) * 2 - 1) * SHADE_SPAN); kept.set(i, v); }
    return v;
  };
}
/** The drift of every tile of one screen and a tile's margin around it, by world pixel. */
export function driftGrid(seedNum, ox, oy, cols, rows) {
  // the control arm for the check that measures the seam: one question per screen, as it was
  if ((globalThis as any).__driftPerScreen) { const one = screenDrift(seedNum, ox, oy); return () => one; }
  const w = cols + 3, grid = new Float32Array(w * (rows + 3));
  for (let ty = -1; ty <= rows + 1; ty++) for (let tx = -1; tx <= cols + 1; tx++) grid[(ty + 1) * w + (tx + 1)] = screenDrift(seedNum, ox + tx, oy + ty);
  return (wx, wy) => {
    let tx = Math.floor(wx / TS) - ox, ty = Math.floor(wy / TS) - oy;
    tx = tx < -1 ? -1 : tx > cols + 1 ? cols + 1 : tx;
    ty = ty < -1 ? -1 : ty > rows + 1 ? rows + 1 : ty;
    return grid[(ty + 1) * w + (tx + 1)];
  };
}

/* THE TURF CARRIES ITS LEVEL'S SHADE. The terraces are shaded by level
 * (`terracePal`: the ground and what grows on it), and the turf is grown over
 * most of the ground -- baked from ONE palette per screen, it painted every
 * level the same green, and a plateau under turf read as flat as the field
 * below it: measured on the island composite, terrace 0 and terrace 1 floors
 * at the same luma either side of a ledge. `palOf(lv)` gives the level's own
 * palette and `levelAt(tx, ty)` the tile's level; the bands are baked per
 * level and each pixel asks its tile. */
/* A PIXEL SINK: the shape of a 2D context (`fillStyle`, one-pixel `fillRect`)
 * over an ImageData, flushed with one putImageData. The turf's roots image
 * is painted a pixel at a time -- 49,152 rects a bake -- and a bake is what
 * every cut and every regrowth asks for now, so the rects had become the
 * costliest thing on a phone's frame. Colours are hex; a cache keeps the
 * parse to once per colour. */
/* `pad` LETS A BLADE GROW OFF THE TOP OF ITS SCREEN (2026-09-08; Vijay: "why is there a seam in the
 * middl here"). A blade layer is one screen, and blades are drawn from the root UPWARD, so the tips
 * of the ones rooted in a screen's first tile row belong three pixels above y=0 -- in the screen
 * above. They were clipped, and the result was a flat line the full width of the window along every
 * horizontal screen boundary: measured in five worlds, the three rows above the boundary scored 10
 * to 30 on per-row crispness against 32 to 79 for the rows around them. The sink now takes a top
 * margin, everything it is told to draw lands `pad` lower in a canvas that is `pad` taller, and the
 * caller blits it that much higher, so the overhang paints over the screen above -- which is drawn
 * first, because `visibleScreens` runs down the rows. */
export function pixelSink(canvas, pad = 0) {
  const ctx = canvas.getContext('2d');
  const w = canvas.width, h = canvas.height;
  const img = ctx ? ctx.createImageData(w, h) : null, d = img ? img.data : null;
  const parsed = new Map();
  let cr = 0, cg = 0, cb = 0, ca = 255, other = null;
  return {
    set fillStyle(v) {
      let c = parsed.get(v);
      if (!c) {
        if (typeof v === 'string' && v[0] === '#' && (v.length === 7 || v.length === 9)) { const n = parseInt(v.slice(1), 16); c = v.length === 7 ? [n >> 16, (n >> 8) & 255, n & 255, 255] : [n >>> 24, (n >> 16) & 255, (n >> 8) & 255, n & 255]; }
        else c = null;
        parsed.set(v, c);
      }
      if (c) { cr = c[0]; cg = c[1]; cb = c[2]; ca = c[3]; other = null; } else other = v;
    },
    fillRect(x, y, rw, rh) {
      if (!d) return;
      if (other !== null) { ctx.fillStyle = other; ctx.fillRect(x, y + pad, rw, rh); return; }   // a colour that is not hex goes the slow way, after the flush below
      y += pad;
      const x0 = Math.max(0, x | 0), y0 = Math.max(0, y | 0), x1 = Math.min(w, (x + rw) | 0), y1 = Math.min(h, (y + rh) | 0);
      for (let yy = y0; yy < y1; yy++) for (let xx = x0; xx < x1; xx++) { const o = (yy * w + xx) * 4; d[o] = cr; d[o + 1] = cg; d[o + 2] = cb; d[o + 3] = ca; }
    },
    flush() { if (ctx && img) ctx.putImageData(img, 0, 0); },
  };
}

export function turfCoverLattice(cols, rows, ox, oy, coverAt, sub = 4) {
  const stride = cols + 2;
  // Keep doubles until the final lattice write, exactly as the direct sampler did.
  const centres = new Float64Array(stride * (rows + 2));
  for (let ty = -1; ty <= rows; ty++) for (let tx = -1; tx <= cols; tx++) {
    centres[(ty + 1) * stride + tx + 1] = 1 - coverAt(ox + tx + 0.5, oy + ty + 0.5);
  }
  const gw = cols * sub + 1, gh = rows * sub + 1;
  const out = new Float32Array(gw * gh);
  for (let gy = 0; gy < gh; gy++) for (let gx = 0; gx < gw; gx++) {
    const fx = gx / sub - 0.5, fy = gy / sub - 0.5;
    const tx0 = Math.floor(fx), ty0 = Math.floor(fy), ax = fx - tx0, ay = fy - ty0;
    const i = (ty0 + 1) * stride + tx0 + 1;
    out[gy * gw + gx] = (centres[i] * (1 - ax) + centres[i + 1] * ax) * (1 - ay)
      + (centres[i + stride] * (1 - ax) + centres[i + stride + 1] * ax) * ay;
  }
  return out;
}

export function bakeTurf(grid, cols, rows, palKey, pal, biome, seedNum, ox, oy, palOf = null, levelAt = null, levelsPx = null, coverAt = null, kindsPx = null) {
  const it = bakeTurfSteps(grid, cols, rows, palKey, pal, biome, seedNum, ox, oy, palOf, levelAt, levelsPx, coverAt, kindsPx);
  let step = it.next();
  while (!step.done) step = it.next();
  return step.value;
}

export function* bakeTurfSteps(grid, cols, rows, palKey, pal, biome, seedNum, ox, oy, palOf = null, levelAt = null, levelsPx = null, coverAt = null, kindsPx = null) {
  const lime = (TURF[biome] || TURF.heath).lime;
  const driftAt = driftGrid(seedNum, ox, oy, cols, rows);   // per tile, not per screen: see the note on `shadeTable`
  /* THE GRASS BELONGS TO THE GROUND IT GROWS ON.
   *
   * These are mixed from `UI.leaf` and `UI.leafLit` — one green for the whole
   * game — and then laid over half of every wet country's tiles. Measured on a
   * render of the whole island: grass averages (170,202,130) against dirt's
   * (111,109,99) and fen's (116,113,129), FIFTY LUMA brighter than every other
   * biome, so the map reads as one neon field plus three interchangeable
   * browns. It is the loudest colour in the game and it is the same colour in
   * four countries.
   *
   * Pulling each tone a third of the way back toward the country's own ground
   * keeps the blades reading as blades — they are still lighter than the soil,
   * which is what makes them legible — while letting heath, bloom and fen be
   * three different greens instead of one. */
  const soil = pal.ground || UI.leafDim;
  /* AND THE PATCH IS PULLED FURTHER ONTO ITS OWN SOIL.
   *
   * At 0.34 the grass sits forty-odd luma above the bare ground it grows on, so
   * the SHAPE of a patch is the loudest thing about it -- the eye reads the
   * outline, not the grass. Half way is still plainly lighter than the soil,
   * which is what makes blades legible, and turns a patch from a shape lying on
   * the ground into a part of it. */
  const toned = (c) => mixHex(c, soil, 0.50);
  const bandsFor = (p) => {
    const tone = (c) => mixHex(c, p.ground || UI.leafDim, 0.50);
    const b0 = tone(mixHex(p.tuft || UI.leafLit, UI.leaf, lime * 0.6));
    const d0 = tone(mixHex(p.shade || UI.leafDim, UI.leafDim, lime * 0.5));
    // one axis for both drifts now: the screen's and the patch's are the same warm/cool shift, so
    // adding them and mixing once is cheaper than mixing twice and leaves no edge between screens
    return { bb: shadeTable((v) => drifted(b0, v, 1)), db: shadeTable((v) => drifted(d0, v, 1)) };
  };
  const byLevel = [];
  for (let lv = 0; lv < 4; lv++) byLevel.push(bandsFor(palOf ? (palOf(lv) || pal) : pal));
  /* THE LEVEL IS ASKED PER PIXEL (LS-218). The patch's tone follows the
   * terrace level, and asked per tile the tone stepped along cell edges: a
   * lighter rectangle with a dark rim wherever a terrace met the next -- the
   * loudest grid on a flat screen, and it was the grass. With a smoothed
   * per-pixel level map the tone boundary rounds its corners and meanders. */
  const lvPx = (wx, wy) => {
    if (!levelsPx) return null;
    const x = wx - ox * TS, y = wy - oy * TS;
    if (x < 0 || y < 0 || x >= cols * TS || y >= rows * TS) return null;
    return levelsPx[y * cols * TS + x];
  };
  const lvOf = (wx, wy) => {
    const l = lvPx(wx, wy);
    if (l !== null && l !== undefined) return Math.max(0, Math.min(3, l | 0));
    return levelAt ? Math.max(0, Math.min(3, levelAt(Math.floor(wx / TS) - ox, Math.floor(wy / TS) - oy) | 0)) : 0;
  };
  const tip0 = toned(mixHex(UI.leaf, '#c8f08a', lime * 0.25));
  const tipT = shadeTable((v) => drifted(tip0, v, 1));
  /* WHERE THE TILE SITS ON THE WARM/COOL AXIS: its own drift plus its patch's, in one step index.
   *
   * THE PATCH KEEPS ITS FIVE STEPS. This file's first line is that grass is PATCHES rather than
   * blades, and the patchiness is that quantisation -- five steps of `PATCH`, so a patch reads as a
   * patch with an edge and not as a gradient. Folding the patch into the fine axis along with the
   * drift dissolved it, which would have been a quiet art regression shipped under a bug fix. So the
   * patch is banded to its five FIRST, exactly as before, and only then is the drift added -- fine,
   * because the drift is a slow field and its whole fault was being asked in steps of a screen. */
  const BANDS = 5;
  const patchField = fieldSampler(seedNum + 733, 1 / 38);
  const bandT = (wx, wy) => {
    const n = patchField(wx, wy);
    return (Math.max(0, Math.min(BANDS - 1, Math.floor(n * BANDS))) / (BANDS - 1)) * 2 - 1;
  };
  const shadeAt = (wx, wy) => shadeStep(driftAt(wx, wy) * DRIFT + bandT(wx, wy) * PATCH);
  const patchBase = (wx, wy) => byLevel[lvOf(wx, wy)].bb(shadeAt(wx, wy));
  const patchDark = (wx, wy) => byLevel[lvOf(wx, wy)].db(shadeAt(wx, wy));
  const W = cols * TS, H = rows * TS;
  const roots = document.createElement('canvas');
  roots.width = W; roots.height = H;
  const gr = pixelSink(roots);   // a pixel a byte, not a pixel a fillRect (LS-227): the roots are 49,152 rects a bake, and a bake is a cut now
  /* WHERE THE PATCH ENDS, ASKED BETWEEN TILES INSTEAD OF AT THEM.
   *
   * `growTurf` answers grassed-or-not once per tile and this used to paint the
   * whole of a grassed tile, eating two to four pixels back on the sides with
   * no grassed neighbour. So a patch's outline was a rectilinear staircase on
   * the 16px grid with a small ragged margin, and a one-tile finger of grass
   * was a SQUARE. On a whole-island render the meadows read as a mosaic of
   * tiles rather than as meadows -- measured as a 2.3x jump in cross-tile
   * contrast exactly on the tile boundary, and reported from play as the map
   * looking "lattice-y".
   *
   * The field the tile test reads is continuous, so it can be asked between
   * tiles as well: a PIXEL is grass when the field there clears that tile's own
   * threshold. The outline is then the field's own contour and owes nothing to
   * the grid. It also crosses the boundary in both directions -- grass reaches
   * into a bare tile where the contour does, which the erosion could never do,
   * and that is half of what made the staircase.
   *
   * Sampled every four pixels and interpolated, not per pixel: the field is six
   * lattice lookups deep, and asking it 49,152 times a screen would put the
   * cost of a screen load into the bake. A 4px lattice under a bilinear
   * interpolation is smooth well below the size of anything the eye reads here.
   */
  const SUB = 4, STEP = TS / SUB;
  const gw = cols * SUB + 1, gh = rows * SUB + 1;
  const F = new Float32Array(gw * gh);
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      F[gy * gw + gx] = turfNoise(seedNum, ox + gx / SUB, oy + gy / SUB);
    }
  }
  /* ...AND SO IS THE THRESHOLD (LS-218). The cover -- how much of the ground
   * the grass takes -- comes from the biome blend, which is continuous, but
   * it was asked once per tile; where two biomes meet the threshold stepped
   * at the cell edge, and wherever the field sat between the two thresholds
   * the patch's outline ran dead straight along that edge: a lighter
   * rectangle with a dark rim. Sampled on the same four-pixel lattice as
   * the field and interpolated, the threshold is a contour of its own. */
  const C = coverAt ? turfCoverLattice(cols, rows, ox, oy, coverAt, SUB) : null;
  /* THE DEEP-MEADOW BONUS IS A FIELD TOO. A cell wholly inside the meadow
   * (`cell.tall` with `inside` at one) added half a point to every column of
   * itself and nothing to its neighbour's, so the fringe leapt at the cell
   * edge; sampled on the same lattice and interpolated, the bonus slopes
   * across the edge like the threshold does (LS-218). */
  const B = new Float32Array(gw * gh);
  for (let gy = 0; gy < gh; gy++) for (let gx = 0; gx < gw; gx++) {
    const cell = grid[Math.min(rows - 1, Math.floor(gy / SUB))] && grid[Math.min(rows - 1, Math.floor(gy / SUB))][Math.min(cols - 1, Math.floor(gx / SUB))];
    B[gy * gw + gx] = cell && cell.tall && cell.inside >= 1 ? 1 : 0;
  }
  const bonusPx = (px0, py0) => {
    const fx = px0 / STEP, fy = py0 / STEP;
    const x0 = Math.min(gw - 2, Math.max(0, Math.floor(fx)));
    const y0 = Math.min(gh - 2, Math.max(0, Math.floor(fy)));
    const ax = fx - x0, ay = fy - y0;
    const a = B[y0 * gw + x0], b = B[y0 * gw + x0 + 1], c = B[(y0 + 1) * gw + x0], d2 = B[(y0 + 1) * gw + x0 + 1];
    return (a * (1 - ax) + b * ax) * (1 - ay) + (c * (1 - ax) + d2 * ax) * ay;
  };
  /* THE THRESHOLD SLOPES BETWEEN TILE CENTRES. Sampling `coverAt` at each
   * lattice point took the tile's own value everywhere inside it, so the
   * lattice held a plateau per tile and the interpolation only had the four
   * pixels astride an edge to climb: the fringe's boundary was a step there.
   * Read at the four nearest tile centres and blended, the threshold climbs
   * across the whole tile and the boundary crosses an edge wherever the
   * field says, not where the edge is (LS-218). */
  const thrPx = (px0, py0) => {
    if (!C) return null;
    const fx = px0 / STEP, fy = py0 / STEP;
    const x0 = Math.min(gw - 2, Math.max(0, Math.floor(fx)));
    const y0 = Math.min(gh - 2, Math.max(0, Math.floor(fy)));
    const ax = fx - x0, ay = fy - y0;
    const a = C[y0 * gw + x0], b = C[y0 * gw + x0 + 1], c = C[(y0 + 1) * gw + x0], d2 = C[(y0 + 1) * gw + x0 + 1];
    return (a * (1 - ax) + b * ax) * (1 - ay) + (c * (1 - ax) + d2 * ax) * ay;
  };
  const fieldPx = (px0, py0) => {
    const fx = px0 / STEP, fy = py0 / STEP;
    const x0 = Math.min(gw - 2, Math.max(0, Math.floor(fx)));
    const y0 = Math.min(gh - 2, Math.max(0, Math.floor(fy)));
    const ax = fx - x0, ay = fy - y0;
    const a = F[y0 * gw + x0], b = F[y0 * gw + x0 + 1];
    const c = F[(y0 + 1) * gw + x0], d2 = F[(y0 + 1) * gw + x0 + 1];
    return (a + (b - a) * ax) * (1 - ay) + (c + (d2 - c) * ax) * ay;
  };
  /* The threshold anywhere on the screen. `grid.thr` holds one per SOIL tile,
   * null where no grass can grow at all -- rock, water, a road -- and those
   * tiles stay bare however the contour runs, because what grows there is a
   * question about the tile, not about the field. */
  const thr = grid.thr;
  const thrAt0 = (tx, ty) => (thr && thr[ty] ? thr[ty][tx] : null);
  /* THE SOIL IS ASKED PER PIXEL TOO (LS-218). A tile with no threshold -- a
   * cliff, a stair, a wall -- painted no grass at all, so the patch stopped
   * dead at the cell where the field's own soil line meanders across it.
   * With the label field, a pixel is soil where the field says so, and a
   * soilless tile borrows the threshold of the nearest tile that has one. */
  const SOILK = new Set([T.GROUND, T.MOSS]);
  const soilPx = (px0, py0) => !kindsPx || (px0 >= 0 && py0 >= 0 && px0 < cols * TS && py0 < rows * TS && SOILK.has(kindsPx[py0 * cols * TS + px0]));
  const thrAt = (tx, ty) => {
    const t = thrAt0(tx, ty);
    if (t !== null && t !== undefined) return t;
    if (!kindsPx) return t;
    for (const [dx, dy] of [[0, -1], [-1, 0], [1, 0], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]]) { const n = thrAt0(tx + dx, ty + dy); if (n !== null && n !== undefined) return n; }
    return null;
  };
  /* AND THE CONTOUR FRAYS. A field contour drawn straight is a SMOOTH CURVE,
   * and a smooth curve at sixteen pixels reads as a painted blob with a
   * vector outline -- the staircase was wrong in one direction and this is
   * wrong in the other. A pixel of noise added to the field before the test
   * breaks the line into single pixels of grass and soil along it, which is
   * how a stand of grass ends.
   *
   * The amount is small because the field is SHALLOW where cover is nearly met
   * over a wide area, and there the fray spreads as far as it can reach: 0.012
   * came out as a fifteen-pixel band of static along one contour. This is one
   * to two pixels where the contour is crisp and a few where it is lazy, which
   * is how a stand thins out rather than ends. World-hashed, so it does not
   * crawl and is the same from either side of a screen seam. */
  /* TALL GRASS IS DECIDED PER COLUMN (LS-218). `growTurf` marks a whole cell
   * tall when the field at its centre clears the threshold by a margin, and
   * the fringe that rises from every tall cell's top edge then drew the
   * boundary of the tall stand along cell edges: a dark rim with a lit line
   * over it, in rectangles -- the loudest grid in the meadows. The same
   * margin is asked here per column of the field, so the stand's edge is
   * the field's own contour; the cell keeps its state (cut, bent, regrowing). */
  const tallCols: any = [];
  for (let ty = 0; ty < rows; ty++) {
    const row: any = [];
    for (let tx = 0; tx < cols; tx++) {
      let cell = grid[ty] && grid[ty][tx];
      let t0 = thrAt(tx, ty);
      /* A CELL BORROWED FOR THE FRINGE. A tile with no grass cell of its own
       * drew no blades at all, so a meadow's fringe stopped dead on the last
       * cell's edge whatever the field said. Where the field's soil reaches
       * into such a tile it borrows the nearest cell beside it for the
       * blades' state and lets its own columns decide (LS-218). */
      if ((!cell || t0 === null || t0 === undefined) && kindsPx) {
        for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
          const nc = grid[ty + dy] && grid[ty + dy][tx + dx], nt = thrAt(tx + dx, ty + dy);
          if (nc && nt !== null && nt !== undefined) { cell = { inside: nc.inside, tall: nc.tall, push: 0, cut: 0, borrowed: true }; t0 = nt; break; }
        }
        if (cell && cell.borrowed) { if (!grid.borrow) grid.borrow = []; if (!grid.borrow[ty]) grid.borrow[ty] = []; grid.borrow[ty][tx] = cell; }
      }
      if (!cell || t0 === null || t0 === undefined) { row.push(null); continue; }
      const wet = cell.inside !== undefined && cell.tall && cell.inside >= 0.999 ? 0 : 0;   // the waterline bonus is the cell's own; kept in `cell.tall`
      const cols16 = new Uint8Array(TS);
      const ins16 = new Float32Array(TS);
      let any = false;
      for (let x = 0; x < TS; x++) {
        const sx0 = tx * TS + x, sy0 = ty * TS + TS / 2;
        const tp = thrPx(sx0 + 0.5, sy0 + 0.5);
        const t = tp === null ? t0 : tp;
        const inside = Math.min(1, (fieldPx(sx0 + 0.5, sy0 + 0.5) - t) / 0.22) + 0.5 * bonusPx(sx0 + 0.5, sy0 + 0.5) + wet;
        const tall = inside > TALL_AT && soilPx(sx0, sy0);
        cols16[x] = tall ? 1 : 0; if (tall) any = true;
        ins16[x] = Math.max(0, Math.min(1, inside));
      }
      // THE HEIGHT IS THE COLUMN'S, NOT THE CELL'S. `bladeHeights` scaled a
      // blade by how far inside the meadow its CELL sat, so at a cell edge the
      // fringe jumped from tall to short in one pixel, a ruled line down the
      // field; the column's own depth in the field makes it a slope (LS-218).
      cell.insidePx = ins16;
      row.push(cols16);   // always the field's answer, even when it is 'no column of this cell is tall': the cell's own flag would draw the fringe square again
    }
    tallCols.push(row);
  }
  grid.tallCols = tallCols;
  yield;
  const FRAY = 0.005;
  const covered = (px0, py0) => {
    const tx = Math.floor(px0 / TS), ty = Math.floor(py0 / TS);
    if (tx < 0 || ty < 0 || tx >= cols || ty >= rows) return false;
    const t0 = thrAt(tx, ty);
    if (t0 === null || t0 === undefined) return false;
    if (!soilPx(px0, py0)) return false;
    const tp = thrPx(px0 + 0.5, py0 + 0.5);
    const t = tp === null ? t0 : tp;
    const wx = ox * TS + px0, wy = oy * TS + py0;
    return fieldPx(px0 + 0.5, py0 + 0.5)
      + (px(seedNum + 51, wx, wy) - 0.5) * FRAY >= t;
  };
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      if (thrAt(tx, ty) === null || thrAt(tx, ty) === undefined) continue;
      // a bare tile is still walked: the contour reaches into it from a
      // grassed neighbour, and stopping at the tile edge is the staircase
      // the tile's threshold hoisted out of the pixel loop: `covered` re-derives
      // the tile from the pixel, which is what the sparse passes below need and
      // what this one would pay 256 times a tile for
      const t0 = thrAt(tx, ty);
      for (let y = 0; y < TS; y++) {
        for (let x = 0; x < TS; x++) {
          const sx0 = tx * TS + x, sy0 = ty * TS + y;
          const wx = ox * TS + sx0, wy = oy * TS + sy0;
          if (!soilPx(sx0, sy0)) continue;
          const tp = thrPx(sx0 + 0.5, sy0 + 0.5);
          if (fieldPx(sx0 + 0.5, sy0 + 0.5)
            + (px(seedNum + 51, wx, wy) - 0.5) * FRAY < (tp === null ? t0 : tp)) continue;
          // SOLID, then blades over it. A 24% random dither is television
          // static, not grass: what makes a lawn read as a lawn is that it is
          // continuous, with short strokes lying in it.
          // inside a mown swath the ground itself goes a shade of straw (LS-236): a cut patch reads from across the screen, not only up close
          const cc = grid[ty][tx];
          gr.fillStyle = cc && cc.cut > 0 && mownAt(cc, sx0, sy0) ? mixHex(patchBase(wx, wy), '#e6dcaa', 0.12 * Math.min(1, cc.cut / (cc.cut0 || 480))) : patchBase(wx, wy);
          gr.fillRect(sx0, sy0, 1, 1);
        }
      }
      if (!grid[ty][tx]) continue;
      /* A MOWN CELL LOOKS MOWN (LS-236): the tufts are cut short and a few
       * pale cut stalks lie where they fell; as the cell regrows the tufts
       * lengthen back. This is the picture the blades stand on, repainted
       * the frame a cut is made and on the regrowth beat. */
      const cell = grid[ty][tx];
      const mown = cell.cut > 0 ? Math.min(1, cell.cut / (cell.cut0 || 480)) : 0;
      if (mown > 0.15) {
        gr.fillStyle = mixHex(patchBase(ox * TS + tx * TS + 8, oy * TS + ty * TS + 8), '#e6dcaa', 0.3 * mown);
        for (let i = 0; i < 6; i++) {
          const cx0 = tx * TS + Math.floor(px(seedNum + 171 + i, tx, ty) * (TS - 3)), cy0 = ty * TS + Math.floor(px(seedNum + 179 + i, ty, tx) * (TS - 1));
          if (!mownAt(cell, cx0, cy0)) continue;   // only inside the swath
          const len = 2 + (i & 1);
          for (let k = 0; k < len; k++) if (covered(cx0 + k, cy0)) gr.fillRect(cx0 + k, cy0, 1, 1);   // a stalk lying across
        }
      }
      // ...AND THE STROKES THAT MAKE IT GRASS. Two or three pixels tall, in
      // the shade tone, scattered through the patch: short marks lying in a
      // continuous mass is what the eye reads as blades, where isolated marks
      // on bare ground read as specks of dirt.
      for (let i = 0; i < 7; i++) {
        const bx = Math.floor(px(seedNum + 131 + i, tx, ty) * TS);
        const by = Math.floor(px(seedNum + 137 + i, ty, tx) * TS);
        const sx0 = tx * TS + bx, sy0 = ty * TS + by;
        if (!covered(sx0, sy0)) continue;
        const wx0 = ox * TS + sx0, wy0 = oy * TS + sy0;
        const h = Math.max(1, Math.round((2 + (px(seedNum + 139, wx0, wy0) < 0.4 ? 1 : 0)) * (1 - (mownAt(cell, sx0, sy0) ? 0.7 * mown : 0))));   // shorter inside the swath while mown (LS-236)
        for (let k = 0; k < h; k++) {
          const yy = sy0 - k;
          if (yy < 0 || !covered(sx0, yy)) break;
          gr.fillStyle = patchDark(wx0, wy0);
          gr.fillRect(sx0, yy, 1, 1);
        }
      }
      /* TIPS STANDING PROUD OF THE TOP OF THE STAND -- the only part of grass
       * that reads as individual blades, and the only part that needs to.
       *
       * The top is found by walking down the column until the contour starts,
       * rather than assumed to be the top of a tile with no grassed neighbour
       * above it. Those are different lines now, and the tile edge is the one
       * this pass exists to stop drawing. */
      for (let i = 0; i < 5; i++) {
        const bx = tx * TS + 1 + Math.floor(px(seedNum + 91 + i, tx, ty) * (TS - 2));
        let top = -1;
        for (let y = 0; y < TS; y++) {
          if (covered(bx, ty * TS + y)) { top = ty * TS + y; break; }
        }
        // the top row of the screen cannot see the stand it may belong to on
        // the screen above, and a tip drawn there would be a row of them along
        // the seam -- the exact artefact this pass removes
        if (top < 0 || (top === 0) || covered(bx, top - 1)) continue;
        const wx = ox * TS + bx;
        const h = 2 + Math.floor(px(seedNum + 97, wx, ty) * 2);
        gr.fillStyle = tipT(shadeAt(wx, oy * TS + top));
        for (let k = 0; k < h; k++) gr.fillRect(bx, Math.max(0, top - k), 1, 1);
      }
    }
    // Keep the tile paint order intact, but return control every two tile rows.
    if ((ty + 1) % 2 === 0) yield;
  }
  gr.flush();
  return { roots };
}

/* THE BLADES: A HEIGHT FIELD, DRAWN OVER EVERYTHING.
 *
 * Adapted from the technique in John Wigg's 2D grass shader, which stores
 * blade height per pixel column and has a fragment shader walk down from each
 * point to see whether a blade reaches it. Four things there are worth having
 * and all four survive the translation to sixteen-pixel tiles and no shaders:
 *
 *   - HEIGHT PER COLUMN, not a sprite. Every column of a grassed tile has its
 *     own blade height, so the top of a stand is an irregular line rather than
 *     a repeated stamp.
 *   - WIND AS THREE SUPERIMPOSED SINES at different frequencies, so the field
 *     never visibly loops. Where the wind is strong a blade loses a pixel of
 *     height and its tip takes a lighter hue — in the original that is what
 *     sells the gust, and it is cheaper than moving anything sideways.
 *   - THE PLAYER PUSHES GRASS DOWN WITH THE SAME MECHANISM. Not a separate
 *     system: a body simply subtracts height, with a radial falloff, and the
 *     grass recovers by the height coming back.
 *   - GRASS DRAWN OVER SPRITES. The original has a second pass that hides part
 *     of a scarecrow behind the grass in front of it. Here the blades are
 *     drawn after the bodies, so standing in tall grass buries your legs.
 *
 * What is NOT taken from it is per-pixel colour gradients along the blade;
 * at this scale a blade is four pixels and a gradient is mud. It gets a body
 * colour and a lit tip.
 */
const MAX_BLADE = 12;

/** How many pixels a cut takes off a blade at (x, y) right now: nine inside the swath freshly mown, fewer as it regrows, none outside it or when whole.
 *  The swath is a round of the blade's pass and closes from its edge as the cell regrows, so a mown patch is a patch and not a cross of cells. */
export function cutFlatten(p, x = null, y = null) {
  if (!(p.cut > 0)) return 0;
  const full = p.cut0 || 480, k = Math.min(1, p.cut / full);
  if (x !== null && p.cutR !== undefined) {
    const d = Math.hypot(x - p.cutCx, y - p.cutCy);
    const r = p.cutR * (0.35 + 0.65 * k) + ((x * 7 + y * 13) % 5) - 2;   // a ragged edge, closing as it regrows
    if (d > r) return 0;
  }
  return Math.max(1, Math.round(9 * k));
}
/** Is the pixel (x, y) of a cut cell inside the mown swath right now? */
export function mownAt(p, x, y) { return cutFlatten(p, x, y) > 0; }

/** Wind at a world column, as a 0..1 strength. Three sines, deliberately
 *  incommensurate, so the pattern does not repeat inside a session. */
/** Which way the world's wind blows across the screen, -1 west or 1 east (LS-389): the grass's gust tips lay downwind
 *  of it, so the meadow says which way a raft will go. Set by the game each frame from `systems.wind`. */
let WIND_X = 1;
export function setWindSign(sx: number) { WIND_X = sx < 0 ? -1 : 1; }
export function windAt(wx, frame) {
  const t = frame / 60;
  return 0.5
    + 0.26 * Math.sin(wx * 0.09 + t * 1.7)
    + 0.16 * Math.sin(wx * 0.031 - t * 0.9)
    + 0.08 * Math.sin(wx * 0.21 + t * 2.9);
}

/** Base blade heights for one tile, from its world column, so a stand's
 *  outline is continuous across tile boundaries. */
export function bladeHeights(cell, tx, ty, seedNum, ox, oy) {
  if (cell.h && cell.hPx === !!cell.insidePx) return cell.h;   // recomputed once the turf bake gives the cell its per-column depth
  const h = new Uint8Array(TS);
  for (let i = 0; i < TS; i++) {
    const wx = (ox + tx) * TS + i;
    const n = px(seedNum + 313, wx, (oy + ty) * TS);
    /* HEIGHT RISES WITH THE STAND, IT DOES NOT SWITCH.
     *
     * This was `cell.tall ? MAX_BLADE : 5` -- eleven pixels or five, decided by
     * `inside > 0.55` on a smooth field. A hard threshold on a smooth field is
     * a CONTOUR, and a 2.2x jump across one is a visible one: bloom came out
     * as wide horizontal bands of tall grass with ruled edges between them,
     * corduroy rather than meadow.
     *
     * The field already says how far inside the stand this is. Letting the
     * height follow it keeps the same range and spends it as a slope. */
    const inside = cell.insidePx ? cell.insidePx[i] : cell.inside;   // the column's depth in the field, where the turf baked one
    const top = 6 + (MAX_BLADE - 6) * Math.min(1, inside);
    h[i] = Math.max(3, Math.round((0.6 + n * 0.4) * top * (0.6 + inside * 0.4)));
  }
  cell.h = h; cell.hPx = !!cell.insidePx;
  return h;
}

function bladeBase(seedNum, wx, ty, rank) {
  return ty * TS + 1 + (rank ? 7 : 0) + Math.floor(px(seedNum + 401 + rank * 2, wx, ty * 7) * 7);
}

/** A static world-space highlight field, cached for both ranks of a cell. */
export function bladeHighlights(cell, tx, ty, seedNum, ox, oy) {
  const wx = (ox + tx) * TS, wy = oy * TS;
  const kept = cell.highlights;
  if (kept && kept.seed === seedNum && kept.x === wx && kept.y === wy && kept.row === ty) return kept.bits;
  let bits = 0;
  for (let ii = 0; ii < TS * 2; ii++) {
    const rank = ii >= TS ? 1 : 0, x = wx + ii - rank * TS;
    if (field(seedNum + 6021, x, wy + bladeBase(seedNum, x, ty, rank), 1 / 12) > 0.55) bits |= 1 << ii;
  }
  cell.highlights = { seed: seedNum, x: wx, y: wy, row: ty, bits };
  return bits;
}

/** How far past its own edge a body flattens grass. A body presses down what
 *  it stands ON plus a little spill — it does not mow a clearing. The reach
 *  is the body's own half-width plus this, so a big Warden leaves a bigger
 *  dent than a pod, which is the point of measuring it from the body at all.
 *
 *  IT WAS A FLAT 22px FROM THE TILE CENTRE. Tile centres are 16px apart, so
 *  22 reached the centre of every neighbour and, diagonally, past it — a 13px
 *  player flattened a 3-tile-wide circle and walked around inside a hole. */
export const PUSH_SPILL = 1;

/* HOW FAR PAST THE BODY THE GRASS NOTICES YOU, AND HOW MUCH.
 *
 * `PUSH_REACH` is the distance beyond the body's own half-width over which
 * the halo fades to nothing — one tile, so the falloff is visible without
 * becoming the dent that overlap-area was introduced to fix. `PUSH_HALO` is
 * its ceiling: at 0.34 the blades lean and shorten but never lie flat, so the
 * footprint under the player still reads as the pressed part.
 *
 * Both are bounded by the checks in test/soak.mjs that measure the pressed
 * area of a body standing on a seam and on a corner. Raising `PUSH_HALO`
 * toward 1 turns the halo back into the radius model those checks exist to
 * keep out. */
export const PUSH_REACH = 22;
export const PUSH_HALO = 0.34;

/** Bodies push the grass down. Radial, so a body flattens a circle around
 *  itself rather than a tile-shaped hole, and it recovers on its own. */
export function pushTurf(grid, bodies, cols, rows) {
  let regrown = 0;
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      const p = grid[ty] && grid[ty][tx];
      if (!p) continue;
      const x0 = tx * TS, y0 = ty * TS;
      let want = 0;
      for (const b of bodies) {
        /* HOW MUCH OF THE TILE THE BODY IS ACTUALLY STANDING ON.
         *
         * This measured from the body's CENTRE to the nearest point of the
         * tile, and pressed anything within `w/2 + spill` of it. That is
         * fine while you stand in the middle of a tile and wrong everywhere
         * else: put the centre on a corner where four tiles meet and the
         * distance to all four is ZERO, so all four went completely flat —
         * a 32x32 dent for a 13px body, six times its own area. Tiles are
         * 16px and the body is 13 and at a fractional position, so in play
         * that corner case is not an edge case, it is most frames.
         *
         * Reported six times as the push radius being too big. It was never
         * the radius: the radius is right and it was being applied to the
         * wrong thing. The comment underneath it even described the intended
         * behaviour — "the one you are only leaning into barely stirs" —
         * which distance-from-centre cannot produce, because a body leaning
         * one pixel into a tile has its centre exactly as far from that tile
         * as a body covering half of it.
         *
         * Overlap area says what standing on something means. The body is a
         * box; intersect it with the tile; press in proportion to how much of
         * the tile that covers, against the most it could cover. Standing
         * dead centre still presses one tile flat, a seam presses two firmly,
         * and a corner now stirs four instead of flattening them.
         */
        const half = (b.w || TS) / 2 + PUSH_SPILL;
        const ovx = Math.max(0, Math.min(b.cx + half, x0 + TS) - Math.max(b.cx - half, x0));
        const ovy = Math.max(0, Math.min(b.cy + half, y0 + TS) - Math.max(b.cy - half, y0));
        // NB: no early `continue` on zero overlap. The halo below is the
        // whole point of this pass for tiles the body does NOT stand on, and
        // the first version of it sat under exactly such a `continue` — so it
        // ran only where coverage already applied and changed nothing at all.
        const span = Math.min(half * 2, TS);
        const cover = (ovx <= 0 || ovy <= 0) ? 0 : (ovx * ovy) / (span * span);
        // AND THE AREA IS ALREADY THE FALLOFF. The old radius model squared
        // its result to stop a cone of half-pressed grass trailing the
        // player; an overlap fraction does that on its own, because area
        // falls away quadratically as a box slides off a tile. Squaring it
        // again took a body straddling a seam — genuinely half on each tile —
        // down to a quarter press on both, which reads as grass that does not
        // notice you. Coverage IS the press.
        /* ...AND OUTSIDE THE FOOTPRINT IT FADES INSTEAD OF STOPPING.
         *
         * Coverage is the right model for how hard the grass under you is
         * pressed, and it is the whole story only if you accept a hard edge:
         * a tile the body overlaps by a hair is pressed, the tile beside it is
         * untouched, and the boundary between them is the edge of a 15px box.
         * That reads as a stamped rectangle following the player around, which
         * is what "the push doesn't look good" means — the radius was never
         * the complaint this time, the DISCONTINUITY was.
         *
         * So a halo, smoothstepped over one tile beyond the body and capped
         * low. It stirs; it does not flatten. The press directly underfoot is
         * still coverage and still exactly the body's footprint — this only
         * fills in what used to be a cliff between pressed and standing, so
         * the grass leans away from you rather than ending in a straight
         * line. `max`, not `+`, so it can never deepen the press under the
         * body and re-open the six-times-reported dent. */
        const tcx = x0 + TS / 2, tcy = y0 + TS / 2;
        const d = Math.hypot(b.cx - tcx, b.cy - tcy);
        const u = (PUSH_REACH - (d - half)) / PUSH_REACH;
        const halo = u <= 0 ? 0 : u >= 1 ? PUSH_HALO
          : PUSH_HALO * u * u * (3 - 2 * u);
        want = Math.max(want, Math.min(1, Math.max(cover, halo)));
      }
      if (want > p.push) p.push = want;
      else p.push = Math.max(0, p.push - 0.05);
      if (p.cut > 0) { p.cut--; if (p.cut === 0) regrown++; }   // a tuft standing again: the picture must be painted again (LS-227)
    }
  }
  return regrown;
}

/** A swung blade cuts what it passes through. The grass grows back — this is
 *  a wood, not a lawn — but slowly enough that a path you cut is a path you
 *  can see for a while. */
export function cutTurf(grid, cols, rows, cx, cy, radius, onCut = null) {
  let cut = 0;
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      const p = grid[ty] && grid[ty][tx];
      if (!p || p.cut > 0) continue;
      const d = Math.hypot(tx * TS + TS / 2 - cx, ty * TS + TS / 2 - cy);
      if (d > radius) continue;
      p.cut = 420 + ((tx * 7 + ty * 13) % 120); p.cut0 = p.cut;   // cut0: the regrowth is measured against it, so the blades come back by degrees
      p.cutCx = cx; p.cutCy = cy; p.cutR = radius + 4;   // WHERE the blade passed (LS-236): the swath is a round of that, not a cross of cells
      cut++;
      if (onCut) onCut(tx, ty);   // the game lays the cut grass down as tufts (LS-176)
    }
  }
  return cut;
}

/** Push the turf under `bodies` aside, and let it stand back up. */
export function stirTurf(grid, bodies, cols, rows) {
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      const p = grid[ty] && grid[ty][tx];
      if (!p) continue;
      let touched = null;
      const x = tx * TS, y = ty * TS;
      for (const b of bodies) {
        if (b.x < x + TS && b.x + b.w > x && b.y < y + TS && b.y + b.h > y) { touched = b; break; }
      }
      if (touched) { p.bend = 1; p.dir = touched.cx > x + TS / 2 ? -1 : 1; }
      else if (p.bend > 0) p.bend = Math.max(0, p.bend - 0.09);
    }
  }
}

/** The ground mass, drawn BEFORE the bodies. This is the part of grass that
 *  is floor rather than foliage. */
export function drawTurf(g, grid, baked, cols, rows) {
  if (!baked) return;
  g.drawImage(baked.roots, 0, 0);
}

/** The blades, drawn AFTER the bodies — which is what buries your legs when
 *  you stand in a stand of tall grass, and the whole reason this is a separate
 *  pass. Height per column, wind and push subtracted, tip lit. */
/* DEPTH. A blade rises UPWARD from its base, so it stands in front of
 * anything whose feet are higher up the screen than that base, and behind
 * anything lower down. Drawing the whole blade layer after the bodies got the
 * first half right and the second half wrong: the grass growing in the
 * player's OWN tile rises past their head, so standing in a field put a fringe
 * across their face.
 *
 * So the layer is drawn twice, split on a depth line: bases above it go down
 * before the bodies, bases below it after. The line is the player's feet,
 * because the player is the body you are looking at.
 */
export function drawBlades(g, grid, cols, rows, frame, pal, biome, seedNum, ox, oy,
  depth = null) {
  // Both ranks have bases in tile rows 1..14. Entire tile rows outside
  // this depth pass need neither color sampling nor per-blade work.
  const firstRow = depth?.front ? Math.max(0, Math.floor((depth.at - 14) / TS) + 1) : 0;
  const lastRow = depth && !depth.front ? Math.min(rows - 1, Math.floor((depth.at - 1) / TS)) : rows - 1;
  if (firstRow > lastRow || firstRow >= rows || lastRow < 0) return;
  const lime = (TURF[biome] || TURF.heath).lime;
  // the same drift the bake used, asked the same way -- per tile, so the standing blades match
  // their own roots and neither carries a screen's edge (see the note on `shadeTable`)
  const driftAt = driftGrid(seedNum, ox, oy, cols, rows);
  const tip0 = mixHex(UI.leaf, '#c8f08a', lime * 0.25);
  const bodyT = shadeTable((v) => drifted(mixHex(pal.tuft || UI.leafLit, UI.leaf, lime * 0.6), v, 1));
  const shadeT = shadeTable((v) => drifted(mixHex(pal.shade || UI.leafDim, UI.leafDim, lime * 0.5), v, 1));
  const tipT = shadeTable((v) => drifted(tip0, v, 1));
  // Wind depends on the world column, not the tile row, rank, or color pass.
  const winds: number[] = new Array(cols * TS);
  /* THE GRASS, UPGRADED (Vijay: "grass could use a visual upgrade and react
   * to being cut better"). Three passes, one colour each: the stem's lower
   * half in the shade, its upper half in the body, and small world-space
   * patches of lit tips, so a stand has highlights with a foot in shadow
   * instead of an evenly bright comb. A CUT is seen by
   * degrees: the blades come back as the cell regrows (`cutFlatten`), so a
   * mown patch is stubble for a while and then short grass, not a pop from
   * nothing to full. A gust leans the tip a pixel downwind as well as
   * taking one off. */
  for (const pass of [0, 1, 2]) {
    const tone = pass === 0 ? shadeT : pass === 1 ? bodyT : tipT;
    for (let ty = firstRow; ty <= lastRow; ty++) {
      for (let tx = 0; tx < cols; tx++) {
        const step = shadeStep(driftAt((ox + tx) * TS, (oy + ty) * TS) * DRIFT);
        const tip = tone(step);
        g.fillStyle = tip;
        const p = (grid[ty] && grid[ty][tx]) || (grid.borrow && grid.borrow[ty] && grid.borrow[ty][tx]);   // a borrowed cell draws the fringe the field asked for
        const tc = grid.tallCols && grid.tallCols[ty] ? grid.tallCols[ty][tx] : null;
        if (!p || (tc ? !tc.some((v) => v) : !p.tall)) continue;
        const hs = bladeHeights(p, tx, ty, seedNum, ox, oy);
        const highlights = pass === 2 ? bladeHighlights(p, tx, ty, seedNum, ox, oy) : 0;
        const dull = pass === 2 ? bodyT(step) : tip;
        let ink = tip;
        // THE FRINGE RISES OUT OF THE TOP OF THE TILE. Drawing blades up from
        // the tile's BOTTOM put them inside a mass that already fills the
        // tile, where they are invisible — and left every tile's top rows to
        // the layer below, which banded the whole field horizontally. Rising
        // from the top edge they overlap the tile above, which is exactly what
        // buries the legs of anything standing in the row below.
        /* TWO RANKS A TILE. One comb of blades rising from the top of each
         * tile left the lower half of every tile to the bake's short marks,
         * and a meadow read as rows of fringe on flat ground. A second rank
         * rises from the tile's middle, a little sparser and two pixels
         * shorter, behind the first: the tile fills with grass at two depths. */
        for (let ii = 0; ii < TS * 2; ii++) {
          const rank = ii >= TS ? 1 : 0, i = ii - rank * TS;
          if (tc && !tc[i]) continue;   // this column of the cell is short grass by the field (LS-218)
          const wx = (ox + tx) * TS + i;
          if (rank && px(seedNum + 409, wx, ty * 5) < 0.3) continue;
          // ...AND THE FRINGE IS NOT A RULED LINE. Rising from a constant
          // height in every tile put a row of blade tips across the field
          // every sixteen pixels, which is the treeline fault again in a
          // different layer. The base wanders by a few pixels per column.
          const baseY = bladeBase(seedNum, wx, ty, rank);
          // in front of the bodies, or behind them?
          if (depth !== null) {
            const front = baseY > depth.at;
            if (front !== depth.front) continue;
          }
          const column = tx * TS + i;
          const w = winds[column] ?? (winds[column] = windAt(wx, frame));
          // A GUST TAKES A PIXEL OFF, exactly as in the shader this is from,
          // and lights the tip. Moving the blade sideways instead reads as a
          // wobble; shortening it reads as wind.
          const gust = w > 0.72 ? 1 : 0;
          const flattened = Math.round(p.push * 5) + cutFlatten(p, tx * TS + i, baseY);
          const h = hs[i] - gust - flattened - (rank ? 2 : 0);
          if (h <= 0) { if (pass === 0 && p.cut > 0 && (i & 1) === 0 && mownAt(p, tx * TS + i, baseY)) g.fillRect(tx * TS + i, baseY - 1, 1, 1); continue; }   // stubble where a blade was mown
          const lower = Math.max(1, Math.floor((h - 1) / 2));
          const lean = gust && w > 0.8 ? 1 : 0;   // the tip laid a pixel downwind
          if (pass === 0) {
            if (h > 1) { g.fillRect(tx * TS + i, baseY - lower, 1, lower); if (!rank && (i % 5) === 2 && lower > 1) g.fillRect(tx * TS + i + 1, baseY - lower + 1, 1, lower - 1); }   // the foot, in shadow; every fifth blade a clump two wide
          } else if (pass === 1) {
            if (h - 1 > lower) g.fillRect(tx * TS + i, baseY - h + 1, 1, h - 1 - lower);   // the stem
          } else {
            const bright = (highlights & (1 << ii)) !== 0;
            const color = bright ? tip : dull;
            if (color !== ink) { g.fillStyle = color; ink = color; }
            const length = bright && h - lower > 1 ? 2 : 1;
            if (!lean) g.fillRect(tx * TS + i, baseY - h, 1, length);
            else {
              // The leaning tip moves, but its lower highlight stays on the stem.
              g.fillRect(tx * TS + i + lean * WIND_X, baseY - h, 1, 1);   // ...downwind of the world's wind
              if (length > 1) g.fillRect(tx * TS + i, baseY - h + 1, 1, 1);
            }
          }
        }
      }
    }
  }
}
